import { Link } from '@tanstack/react-router'
import { ArrowLeft, MapPin, Star, Store } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import { ListingDetailActions } from '~/features/listings/components/listing-detail-actions'
import { todayHoursLabel } from '~/features/listings/components/view/listing-hours-view'
import { MetaRow } from '~/features/shared/components/view/meta-row'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import type { Listing } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

const COVER =
  'squircle relative aspect-video w-full shrink-0 overflow-hidden rounded-lg bg-muted md:aspect-auto md:min-h-40 md:w-56 md:self-stretch xl:w-72 2xl:w-80'

type PlaceState = 'draft' | 'importing' | 'published'

function placeState(listing: Listing): PlaceState {
  if (listing.isDraft || listing.status === 'Draft') return 'draft'
  if (listing.status === 'Importing') return 'importing'
  return 'published'
}

const STATE_LABEL: Record<PlaceState, () => string> = {
  draft: m.draft,
  importing: m.placeHeaderImporting,
  published: m.placeHeaderPublished,
}

export function ListingDetailHeader({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: listing, isLoading } = useListing(business?.id ?? '', listingId)

  if (isLoading || !business) {
    return (
      <Card className="flex flex-col gap-5 p-4 sm:gap-6 sm:p-6 md:flex-row">
        <Skeleton className={COVER} />
        <div className="flex flex-1 flex-col gap-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-10 w-full max-w-md" />
          <Skeleton className="mt-auto h-9 w-full max-w-lg" />
        </div>
      </Card>
    )
  }

  if (!listing) {
    return (
      <Card className="flex flex-col items-start gap-3 p-6">
        <p className="font-medium">{m.listingNotFound()}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard/listings">
            <ArrowLeft className="h-4 w-4" />
            {m.backToListings()}
          </Link>
        </Button>
      </Card>
    )
  }

  const state = placeState(listing)
  const address = [listing.location.street, listing.location.city, listing.location.country]
    .filter(Boolean)
    .join(', ')
  const priceLevel =
    listing.priceLevel && listing.priceLevel >= 1 && listing.priceLevel <= 4
      ? listing.priceLevel
      : null

  return (
    <Card className="flex flex-col gap-5 p-4 sm:gap-6 sm:p-6 md:flex-row">
      <div className={COVER}>
        {listing.coverImageUrl ? (
          <img
            src={listing.coverImageUrl}
            alt={listing.name}
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Store className="size-10 text-muted-foreground/40" />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div className="space-y-2">
          <h1 className="text-balance font-semibold text-2xl text-foreground leading-tight tracking-[-0.025em] sm:text-[30px]">
            {listing.name}
          </h1>
          <ul className="space-y-1">
            {listing.location.name && listing.location.name !== listing.name && (
              <MetaRow icon={<Store />}>{listing.location.name}</MetaRow>
            )}
            <MetaRow icon={<MapPin />}>{address}</MetaRow>
          </ul>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:flex sm:flex-wrap sm:gap-x-10">
          <Fact label={m.placeHeaderStatus()}>
            <span className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  state === 'published' ? 'bg-[var(--metric-up)]' : 'bg-muted-foreground/60',
                )}
              />
              {STATE_LABEL[state]()}
            </span>
          </Fact>
          {listing.hours.length > 0 && (
            <Fact label={m.today()}>
              <span className="tabular-nums">{todayHoursLabel(listing.hours)}</span>
            </Fact>
          )}
          {listing.rating !== undefined && listing.rating > 0 && (
            <Fact label={m.placeHeaderRating()}>
              <span className="flex items-center gap-1.5">
                <Star className="size-3.5 shrink-0 fill-current" />
                <span className="tabular-nums">
                  {new Intl.NumberFormat(getLocale(), {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }).format(listing.rating)}
                </span>
                {listing.reviewCount ? (
                  <span className="truncate font-normal text-muted-foreground">
                    {m.nReviews({ count: listing.reviewCount })}
                  </span>
                ) : null}
              </span>
            </Fact>
          )}
          {priceLevel ? <Fact label={m.listingPriceLevel()}>{'€'.repeat(priceLevel)}</Fact> : null}
        </dl>

        <div className="mt-auto border-t pt-4">
          <ListingDetailActions listingId={listingId} />
        </div>
      </div>
    </Card>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-[0.06em]">
        {label}
      </dt>
      <dd className="font-medium text-foreground text-sm">{children}</dd>
    </div>
  )
}
